import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ICategorie } from 'src/app/shared/models/categorie';
import { ICompetence } from 'src/app/shared/models/competence';
import { IEvaluationResponse } from 'src/app/shared/models/evaluation';
import { IUniteOrganisationnelle } from 'src/app/shared/models/up-evaluation';

import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { CompetenceService } from '../../competence-list/competence.service';
import { UniteOrganisationnelleService } from '../../uo-evaluation/uo-evaluation.service';
import { EvaluationService } from '../evaluation-service';

export interface UoSection {
  selectedUoId: string | null;
  selectedCategorieId: string | null;
  uoLibelle: string;
  categoriesFiltrees: ICategorie[];
  competencesFiltrees: ICompetence[];
  allCompetencesUo: ICompetence[];
}

export interface AddLignesDialogData {
  evaluationId: string;
  existingCompetenceIds: string[];
}

@Component({
  selector: 'app-add-lignes-dialog',
  templateUrl: './add-lignes-dialog.component.html',
  styleUrls: ['./add-lignes-dialog.component.less']
})
export class AddLignesDialogComponent implements OnInit {

  isSubmitting = false;
  backendError: string | null = null;

  unitesOrganisationnelles: IUniteOrganisationnelle[] = [];
  uoSections: UoSection[] = [];
  competencesSelectionnees: ICompetence[] = [];

  constructor(
    private evaluationService: EvaluationService,
    private uoService: UniteOrganisationnelleService,
    private competenceService: CompetenceService,
    private snackbarService: SnackbarService,
    public dialogRef: MatDialogRef<AddLignesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddLignesDialogData
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.uoSections = [this.newSection()];
    this.loadUos();
  }

  loadUos(): void {
    this.uoService.getAll().subscribe({
      next: (uos) => { this.unitesOrganisationnelles = uos; },
      error: (err) => {
        this.backendError = this.extractErrorMessage(err, 'Erreur lors du chargement des UO');
      }
    });
  }

  // ── Sections UO ───────────────────────────────────────────────────────────

  newSection(): UoSection {
    return {
      selectedUoId: null,
      selectedCategorieId: null,
      uoLibelle: '',
      categoriesFiltrees: [],
      competencesFiltrees: [],
      allCompetencesUo: []
    };
  }

  addUoSection(): void {
    this.uoSections.push(this.newSection());
  }

  removeUoSection(index: number): void {
    this.uoSections.splice(index, 1);
  }

  onUoChange(section: UoSection): void {
    section.selectedCategorieId = null;
    section.categoriesFiltrees = [];
    section.competencesFiltrees = [];
    section.allCompetencesUo = [];

    const uo = this.unitesOrganisationnelles.find(u => u.id === section.selectedUoId);
    section.uoLibelle = uo ? `${(uo as any).code} — ${(uo as any).libelle}` : '';

    if (!section.selectedUoId) return;

    this.competenceService.getActiveByUo(section.selectedUoId).subscribe({
      next: (competences) => {
        section.allCompetencesUo = competences;
        const catMap = new Map<string, ICategorie>();
        competences.forEach(c => {
          if (!catMap.has(c.categorieId)) {
            catMap.set(c.categorieId, { id: c.categorieId, nom: c.categorieNom });
          }
        });
        section.categoriesFiltrees = Array.from(catMap.values());
      },
      error: (err) => {
        this.backendError = this.extractErrorMessage(err, 'Erreur lors du chargement des compétences');
      }
    });
  }

  onCategorieChange(section: UoSection): void {
    section.competencesFiltrees = section.allCompetencesUo.filter(
      c => c.categorieId === section.selectedCategorieId
    );
  }

  // ── Gestion des compétences sélectionnées ─────────────────────────────────

  addCompetence(comp: ICompetence): void {
    if (this.isAlreadyInEvaluation(comp.id)) return;
    if (!this.isAlreadyAdded(comp.id)) {
      this.competencesSelectionnees.push(comp);
    }
  }

  removeCompetence(id: string): void {
    this.competencesSelectionnees = this.competencesSelectionnees.filter(c => c.id !== id);
  }

  isAlreadyAdded(id: string): boolean {
    return this.competencesSelectionnees.some(c => c.id === id);
  }

  isAlreadyInEvaluation(id: string): boolean {
    return this.data.existingCompetenceIds.includes(id);
  }

  // ── Soumission ────────────────────────────────────────────────────────────

  onSubmit(): void {
    if (this.competencesSelectionnees.length === 0) return;

    this.backendError = null;
    this.isSubmitting = true;

    this.evaluationService.addLignes(this.data.evaluationId, {
      competenceIds: this.competencesSelectionnees.map(c => c.id)
    }).subscribe({
      next: (result: IEvaluationResponse) => {
        this.isSubmitting = false;
        this.snackbarService.openSnackBar(
          `${this.competencesSelectionnees.length} critère(s) ajouté(s) avec succès`
        );
        this.dialogRef.close(result);
      },
      error: (err: any) => {
        this.isSubmitting = false;
        this.backendError = this.extractErrorMessage(err, 'Erreur lors de l\'ajout des critères');
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private extractErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (body?.message && typeof body.message === 'string') return body.message;
    if (typeof body === 'string') return body;
    return fallback;
  }
}